import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import Layout from './components/Layout'
import ClientLayout from './components/ClientLayout'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import Dashboard from './pages/dashboard/Dashboard'
import Cases from './pages/cases/Cases'
import Documents from './pages/documents/Documents'
import Deadlines from './pages/deadlines/Deadlines'
import Workspaces from './pages/workspaces/Workspaces'
import Notifications from './pages/notifications/Notifications'
import Profile from './pages/profile/Profile'
import Search from './pages/search/Search'
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import ProtectedAdminRoute from './components/ProtectedAdminRoute'
import { ClientAuthProvider } from './contexts/ClientAuthContext'
import ProtectedClientRoute from './components/ProtectedClientRoute'
import ClientLogin from './pages/client/Login'
import ClientDashboard from './pages/client/Dashboard'
import ClientCases from './pages/client/Cases'
import AdminDashboard from './pages/admin/AdminDashboard'

// Lazy-loaded client pages for code splitting
const ClientDocuments = lazy(() => import('./pages/client/Documents'))
const ClientDeadlines = lazy(() => import('./pages/client/Deadlines'))
const ClientProfile = lazy(() => import('./pages/client/Profile'))
const ClientNotifications = lazy(() => import('./pages/client/Notifications'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ClientAuthProvider>
          <AuthProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Internal authentication routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Internal protected routes */}
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="cases" element={<Cases />} />
                <Route path="documents" element={<Documents />} />
                <Route path="deadlines" element={<Deadlines />} />
                <Route path="workspaces" element={<Workspaces />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="profile" element={<Profile />} />
                <Route path="search" element={<Search />} />
                {/* Admin route within the same layout */}
                <Route path="admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
              </Route>

              {/* Client portal routes */}
              <Route path="/client/login" element={<ClientLogin />} />
              <Route path="/client" element={<ProtectedClientRoute><ClientLayout /></ProtectedClientRoute>}>
                <Route index element={<Navigate to="/client/dashboard" replace />} />
                <Route path="dashboard" element={<ClientDashboard />} />
                <Route path="cases" element={<ClientCases />} />
                <Route path="documents" element={
                  <Suspense fallback={<div className="p-6">Loading documents...</div>}>
                    <ClientDocuments />
                  </Suspense>
                } />
                <Route path="deadlines" element={
                  <Suspense fallback={<div className="p-6">Loading deadlines...</div>}>
                    <ClientDeadlines />
                  </Suspense>
                } />
                <Route path="notifications" element={
                  <Suspense fallback={<div className="p-6">Loading notifications...</div>}>
                    <ClientNotifications />
                  </Suspense>
                } />
                <Route path="profile" element={
                  <Suspense fallback={<div className="p-6">Loading profile...</div>}>
                    <ClientProfile />
                  </Suspense>
                } />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </ClientAuthProvider>
      </Router>
    </QueryClientProvider>
  )
}

export default App